import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  adminLocaleEditor, adminSaveOverride, adminExportI18nCsv, adminImportI18nCsv, adminListLocales,
} from "@/lib/apiClient";
import { ArrowLeft, Search, Upload, Download, ExternalLink, Check, RotateCcw, Loader2 } from "lucide-react";

/**
 * Per-language translation editor. Grouped by Menu (the same structure used
 * in the CSV export, §4) so an admin can jump straight to "everything on the
 * Module 8 screen" instead of scrolling an alphabetical wall of ~900 keys.
 * Row click expands an inline override editor directly beneath it — the
 * same accordion pattern as GroupsEditor.jsx, no scroll-to-top.
 */
export default function AdminTranslationEditor() {
  const { code } = useParams();
  const [locale, setLocale] = useState(null);
  const [items, setItems] = useState(null);
  const [query, setQuery] = useState("");
  const [menuFilter, setMenuFilter] = useState("all");
  const [unreviewedOnly, setUnreviewedOnly] = useState(false);
  const [openKey, setOpenKey] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const [importing, setImporting] = useState(false);

  const load = () => adminLocaleEditor(code).then((d) => setItems(d.items));
  useEffect(() => { load(); setOpenKey(null); }, [code]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    adminListLocales().then((d) => setLocale(d.locales.find((l) => l.code === code) || null));
  }, [code]);

  const menus = useMemo(() => {
    if (!items) return [];
    return [...new Set(items.map((i) => i.menu))].sort();
  }, [items]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = query.trim().toLowerCase();
    return items.filter((i) => {
      if (menuFilter !== "all" && i.menu !== menuFilter) return false;
      if (unreviewedOnly && i.status !== "unreviewed") return false;
      if (!q) return true;
      return (
        i.key.toLowerCase().includes(q) ||
        (i.en || "").toLowerCase().includes(q) ||
        (i.base || "").toLowerCase().includes(q) ||
        (i.override || "").toLowerCase().includes(q)
      );
    });
  }, [items, query, menuFilter, unreviewedOnly]);

  const openRow = (item) => {
    if (openKey === item.key) { setOpenKey(null); return; }
    setOpenKey(item.key);
    setDraft(item.override ?? item.base ?? "");
  };

  const save = async (item) => {
    setSaving(true);
    try {
      await adminSaveOverride(code, item.key, draft);
      toast.success("Saved");
      setOpenKey(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const resetToBase = async (item) => {
    setSaving(true);
    try {
      await adminSaveOverride(code, item.key, "");
      toast.success("Reverted to base translation");
      setOpenKey(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setSaving(false); }
  };

  const exportCsv = async () => {
    try { await adminExportI18nCsv(code); }
    catch { toast.error("Export failed"); }
  };

  const importCsv = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const r = await adminImportI18nCsv(code, file);
      toast.success(`Imported ${r.updated} overrides${r.skipped_unknown_key ? ` (${r.skipped_unknown_key} unknown keys skipped)` : ""}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!items) return <div className="h-40 bg-[#E7E0D2] rounded-2xl animate-pulse" />;

  const reviewedCount = items.filter((i) => i.status === "admin_reviewed").length;

  return (
    <div className="space-y-4" data-testid="admin-translation-editor">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/admin/translations" className="inline-flex items-center gap-1 text-xs text-[#4B5563] hover:text-[#064E3B]">
            <ArrowLeft className="w-3.5 h-3.5" /> All languages
          </Link>
          <div className="text-lg font-heading font-semibold text-[#1F2937] mt-1">
            {locale?.label || code} <span className="text-sm font-normal text-[#4B5563]">({reviewedCount}/{items.length} reviewed)</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a href={`${window.location.origin}/dashboard?preview_lang=${code}`} target="_blank" rel="noreferrer"
            data-testid="i18n-preview-link"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937]">
            <ExternalLink className="w-3.5 h-3.5" /> Preview live
          </a>
          <button onClick={exportCsv} data-testid="i18n-export-btn"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937]">
            <Download className="w-3.5 h-3.5" /> Export CSV
          </button>
          <label className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937] cursor-pointer">
            {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Import CSV
            <input ref={fileRef} type="file" accept=".csv" hidden data-testid="i18n-import-input"
              onChange={(e) => importCsv(e.target.files?.[0])} />
          </label>
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 text-[#9CA3AF] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="i18n-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search key, English, or translation…"
            className="w-full pl-8 pr-3 py-2 rounded-lg border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm" />
        </div>
        <select data-testid="i18n-menu-filter" value={menuFilter} onChange={(e) => setMenuFilter(e.target.value)}
          className="px-3 py-2 rounded-lg border border-[#E7E0D2] focus:border-[#064E3B] outline-none text-sm bg-white">
          <option value="all">All screens</option>
          {menus.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <label className="inline-flex items-center gap-1.5 text-xs text-[#4B5563] px-3 py-2 rounded-lg border border-[#E7E0D2] cursor-pointer">
          <input type="checkbox" checked={unreviewedOnly} onChange={(e) => setUnreviewedOnly(e.target.checked)} />
          Unreviewed only
        </label>
        <span className="text-xs text-[#4B5563]">{filtered.length} of {items.length}</span>
      </div>

      <div className="rounded-2xl border border-[#E7E0D2] bg-white overflow-hidden divide-y divide-[#E7E0D2]">
        {filtered.slice(0, 400).map((item) => (
          <div key={item.key} data-testid={`i18n-row-${item.key}`}>
            <button onClick={() => openRow(item)} className="w-full text-left px-4 py-3 hover:bg-[#FDFBF7] transition flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider text-[#4B5563]">{item.menu}{item.submenu ? ` · ${item.submenu}` : ""}</span>
                  <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full font-medium ${
                    item.status === "admin_reviewed" ? "bg-[#ECFDF5] text-[#064E3B]" : "bg-[#FFF7ED] text-[#D97706]"
                  }`}>{item.status === "admin_reviewed" ? "reviewed" : "unreviewed"}</span>
                </div>
                <div className="text-sm text-[#1F2937] mt-0.5 truncate">{item.en}</div>
                <div className="text-xs text-[#4B5563] mt-0.5 truncate" dir={locale?.dir || "ltr"}>
                  {item.override || item.base || <span className="italic text-[#9CA3AF]">— falls back to English —</span>}
                </div>
              </div>
              <span className="text-[10px] text-[#9CA3AF] font-mono shrink-0">{item.key}</span>
            </button>

            {openKey === item.key && (
              <div className="px-4 pb-4 bg-[#FDFBF7]" data-testid={`i18n-editor-${item.key}`}>
                <div className="text-xs text-[#4B5563] mb-1">English (baseline)</div>
                <div className="text-sm text-[#1F2937] bg-white border border-[#E7E0D2] rounded-lg px-3 py-2 mb-2">{item.en}</div>
                {item.base && (
                  <>
                    <div className="text-xs text-[#4B5563] mb-1">Base translation ({item.base_source || "machine"})</div>
                    <div className="text-sm text-[#1F2937] bg-white border border-[#E7E0D2] rounded-lg px-3 py-2 mb-2" dir={locale?.dir || "ltr"}>{item.base}</div>
                  </>
                )}
                <div className="text-xs text-[#4B5563] mb-1">Override — this is what students see when set</div>
                <textarea
                  data-testid={`i18n-override-input-${item.key}`}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  dir={locale?.dir || "ltr"}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-[#064E3B] outline-none text-sm resize-none bg-white"
                />
                <div className="mt-2 flex items-center gap-2">
                  <button data-testid={`i18n-save-${item.key}`} onClick={() => save(item)} disabled={saving}
                    className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#064E3B] text-white hover:bg-[#047857] disabled:opacity-50">
                    <Check className="w-3.5 h-3.5" /> Save override
                  </button>
                  {item.override && (
                    <button data-testid={`i18n-reset-${item.key}`} onClick={() => resetToBase(item)} disabled={saving}
                      className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#E7E0D2] hover:border-[#064E3B] text-[#1F2937] disabled:opacity-50">
                      <RotateCcw className="w-3.5 h-3.5" /> Reset to base
                    </button>
                  )}
                  <button onClick={() => setOpenKey(null)} className="text-xs px-3 py-1.5 rounded-full text-[#4B5563] hover:text-[#1F2937]">
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
        {filtered.length > 400 && (
          <div className="px-4 py-3 text-xs text-[#4B5563] text-center">
            Showing the first 400 matches — narrow your search or pick a screen to see more.
          </div>
        )}
        {filtered.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-[#4B5563]">No keys match.</div>
        )}
      </div>
    </div>
  );
}
